import { useQuery } from "@tanstack/react-query";

export type CapacityStatus = "over" | "near" | "ok" | "unknown";

export interface LocationSummaryData {
  location: string;
  locationLabel: string;
  occupancyAllZones: number;
  capacityConfiguredZones: number;
  occupancyConfiguredZones: number;
  utilizationConfigured: number | null;
  status: CapacityStatus;
  zonesTotal: number;
  zonesWithCapacity: number;
  zonesWithoutCapacity: number;
  animalsInUnconfiguredZones: number;
  dataAsOf: string | null;
  isStale: boolean;
  noReportsWarning: boolean;
}

export interface SystemSummaryData {
  occupancyAllZones: number;
  capacityConfiguredZones: number;
  occupancyConfiguredZones: number;
  utilizationConfigured: number | null;
  status: CapacityStatus;
  zonesTotal: number;
  zonesWithCapacity: number;
  zonesWithoutCapacity: number;
  animalsInUnconfiguredZones: number;
  dataAsOf: string | null;
  staleLocationCount: number;
  currentLocationCount: number;
  noReportsLocationCount: number;
}

export interface CapacitySummaryResponse {
  systemSummary: SystemSummaryData;
  locationSummaries: LocationSummaryData[];
}

export interface ZoneCapacityData {
  zoneCode: string;
  areaLabel: string;
  occupancy: number;
  capacity: number | null;
  status: CapacityStatus;
}

export interface CapacityLocationSummary {
  totalAnimals: number;
  zonesWithCapacity: number;
  zonesWithoutCapacity: number;
  dataAsOf: string | null;
  staleCutoff: string;
  isStale: boolean;
  noReportsWarning: boolean;
}

export interface CapacityLocationResponse {
  zones: ZoneCapacityData[];
  summary: CapacityLocationSummary;
}

export function useCapacitySummary() {
  return useQuery<CapacitySummaryResponse>({
    queryKey: ["/api/capacity/summary"],
  });
}

export function useCapacityLocation(location: string | null) {
  return useQuery<CapacityLocationResponse>({
    queryKey: [`/api/capacity?location=${encodeURIComponent(location || "")}`],
    enabled: !!location,
  });
}
